import { useEffect } from "react";

import { preloadDashboard } from "@luzmo/react-embed";
import InsightsIcon from "@mui/icons-material/Insights";
import { IconButton, Tooltip, Zoom } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { EmbeddedDashboardName } from "components/EmbeddedAnalytics/types";
import { NAV_ROUTES } from "components/NavigationBar/routes";
import { LUZMO_APP_HOST } from "constants/index";
import { useEmbeddedAnalyticsAccess, useRoleContext } from "hooks";

/**
 * The Data nav button, which also warms Luzmo's dashboard bundle.
 *
 * That bundle is 221 requests and ~3.5 MB, none of it cacheable today, and it
 * does not start downloading until an embed mounts — so opening the data page
 * cold means waiting several seconds for it. This button is on screen from the
 * moment a shipper lands anywhere in the app, so starting the download here
 * gives it minutes of lead time instead of none, and the module stays in memory
 * for the rest of the SPA session.
 *
 * Gated on entitlement rather than fired for every shipper: only enabled
 * companies can open these dashboards, and the rest should not pay for 3.5 MB
 * they will never use. The entitlement request is SWR-cached, so it costs one
 * call per session — one the data page would make anyway.
 */
export function DataPageNavBarButton() {
  const { role } = useRoleContext();
  const navigate = useNavigate();
  const { hasAccess } = useEmbeddedAnalyticsAccess({
    dashboard: EmbeddedDashboardName.COMPANY_DASHBOARD,
  });

  useEffect(() => {
    // Effects run after first paint and nothing awaits this, so it never holds
    // the page up. Started as early as entitlement allows rather than on idle:
    // lead time is the whole mechanism, and a busy app might not go idle before
    // the shipper clicks through. Repeat calls are free — the SDK caches the
    // remote per app server.
    if (hasAccess) {
      preloadDashboard(LUZMO_APP_HOST);
    }
  }, [hasAccess]);

  const onClick = () => {
    navigate(NAV_ROUTES.DATA_PAGE);
  };

  if (role?.type === "SHIPPER") {
    return (
      <Tooltip title="Data" placement="right" TransitionComponent={Zoom}>
        <IconButton color="inherit" onClick={onClick}>
          <InsightsIcon fontSize="large" />
        </IconButton>
      </Tooltip>
    );
  }

  return <div />;
}
